import * as React from 'react'

import { Button, Flex, Skeleton, Text } from '@buffer-mono/popcorn'

import { MessageBubble } from './MessageBubble'
import {
  findFirstUnreadIndex,
  groupMessages,
  showsAvatar,
} from './threadDisplay'
import type { UseConversationMessagesQueryReturn } from '../hooks/useConversationMessagesQuery'

import styles from './MessageThread.module.css'

import type { JSX } from 'react'

type MessageThreadProps = Pick<
  UseConversationMessagesQueryReturn,
  'messages' | 'loading' | 'error'
> & {
  /** The open conversation; a change scrolls the new thread to its end. */
  conversationId: string
  /**
   * From the conversation's read state. Places the "New" separator before the
   * first inbound message the reader has not seen. `null` means never read;
   * `undefined` means the row is not loaded yet, and no separator shows until
   * it is, so a stale one never flashes on a deep link.
   */
  lastReadAt: string | null | undefined
  onRetry: () => void
  /** Reference time for the date lines. Defaults to now; tests pin it. */
  now?: Date
}

/**
 * The middle of the thread pane: a conversation's messages, oldest at the top
 * and the latest at the bottom, where the pane opens.
 *
 * Messages are split into groups, each under one centred date line, and the
 * "New" separator marks where unread messages start. Takes the query result as
 * props so the view owns one data source and stories and tests can render
 * every state without Apollo.
 */
export function MessageThread({
  messages,
  loading,
  error,
  conversationId,
  lastReadAt,
  onRetry,
  now,
}: MessageThreadProps): JSX.Element {
  const scrollRef = useScrollToLatest(conversationId, messages.length > 0)

  if (error) {
    return <ThreadError onRetry={onRetry} />
  }

  if (loading && messages.length === 0) {
    return <ThreadSkeleton />
  }

  if (messages.length === 0) {
    return <ThreadEmpty />
  }

  const groups = groupMessages(messages, now)
  const firstUnread =
    lastReadAt === undefined ? null : findFirstUnreadIndex(messages, lastReadAt)
  let index = 0

  return (
    <section
      ref={scrollRef}
      className={styles.scroller}
      aria-label="Conversation messages"
    >
      <div className={styles.thread}>
        {groups.map((group) => {
          const groupStart = index
          index += group.messages.length
          const key = group.messages[0].id
          return (
            <React.Fragment key={key}>
              {group.dateLabel && (
                <Text
                  as="p"
                  size="sm"
                  color="subtle"
                  weight="medium"
                  align="center"
                  className={styles.dateLine}
                >
                  {group.dateLabel}
                </Text>
              )}
              <Flex
                as="ul"
                direction="column"
                align="stretch"
                gap="xs"
                className={styles.messages}
                aria-label={
                  group.dateLabel ? `Messages, ${group.dateLabel}` : 'Messages'
                }
              >
                {group.messages.map((message, offset) => {
                  const position = groupStart + offset
                  return (
                    <React.Fragment key={message.id}>
                      {position === firstUnread && <NewSeparator />}
                      <MessageBubble
                        message={message}
                        showAvatar={showsAvatar(messages, position)}
                      />
                    </React.Fragment>
                  )
                })}
              </Flex>
            </React.Fragment>
          )
        })}
      </div>
    </section>
  )
}

/**
 * Pins the view to the newest message once per conversation visit: as soon
 * as the thread has rows, and again whenever another conversation opens.
 * Keyed on rows being present rather than on the query settling, so a cached
 * thread pins on its first paint instead of jumping when the background
 * refresh completes. The pin is armed again on every conversation change, so
 * returning to a thread after an empty or failed one pins it afresh. Later
 * updates leave the scroll position alone so reading older messages is not
 * interrupted.
 */
function useScrollToLatest(
  conversationId: string,
  hasMessages: boolean,
): React.RefObject<HTMLElement> {
  const scrollRef = React.useRef<HTMLElement>(null)
  const visitRef = React.useRef<{ conversationId: string; pinned: boolean }>({
    conversationId,
    pinned: false,
  })

  React.useLayoutEffect(() => {
    if (visitRef.current.conversationId !== conversationId) {
      visitRef.current = { conversationId, pinned: false }
    }
    if (visitRef.current.pinned || !hasMessages || !scrollRef.current) return
    visitRef.current.pinned = true
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [conversationId, hasMessages])

  return scrollRef
}

/** A rule with "New" in the middle, as a list item so it sits between bubbles. */
function NewSeparator(): JSX.Element {
  return (
    <Flex
      as="li"
      align="center"
      gap="xs"
      className={styles.separator}
      aria-label="New messages"
    >
      <Text as="span" size="sm" color="subtle" weight="medium">
        New
      </Text>
    </Flex>
  )
}

/**
 * Placeholder rows shaped like a short thread, top to bottom: the other
 * person's bubbles on the left with the avatar closing their run, ours on the
 * right. Heights match a one-line and a two-line bubble.
 */
const SKELETON_ROWS = [
  {
    slotId: 'inbound-long',
    isOwn: false,
    lines: 2,
    width: '280px',
    avatar: false,
  },
  {
    slotId: 'inbound-short',
    isOwn: false,
    lines: 1,
    width: '180px',
    avatar: true,
  },
  { slotId: 'own-short', isOwn: true, lines: 1, width: '220px', avatar: false },
  { slotId: 'own-long', isOwn: true, lines: 2, width: '300px', avatar: false },
  {
    slotId: 'inbound-last',
    isOwn: false,
    lines: 1,
    width: '140px',
    avatar: true,
  },
] as const

/** A bubble's height: its padding around one or two lines of body text. */
const BUBBLE_HEIGHT = { 1: '2.425rem', 2: '3.85rem' } as const

/**
 * The thread while its first page loads: placeholder bubbles where the
 * messages will be, sitting at the bottom of the pane where a loaded thread
 * opens. Popcorn's skeleton stays invisible for its reveal delay, so a fast
 * load goes straight from an empty pane to the messages without a flash.
 */
function ThreadSkeleton(): JSX.Element {
  return (
    <Flex
      direction="column"
      justify="end"
      align="stretch"
      gap="xs"
      className={styles.skeleton}
      aria-label="Loading messages"
      aria-busy
    >
      {SKELETON_ROWS.map((row) => (
        <Flex
          key={row.slotId}
          align="end"
          justify={row.isOwn ? 'end' : 'start'}
          gap="xs"
        >
          {!row.isOwn && (
            <Flex as="span" className={styles.avatarSlot}>
              {row.avatar && <Skeleton width="24px" height="24px" rounded />}
            </Flex>
          )}
          <Skeleton
            width={row.width}
            height={BUBBLE_HEIGHT[row.lines]}
            className={styles.skeletonBubble}
          />
        </Flex>
      ))}
    </Flex>
  )
}

function ThreadEmpty(): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="xs"
      className={styles.state}
    >
      <Text weight="medium">No messages yet</Text>
      <Text color="subtle" align="center" size="sm">
        Messages in this conversation will show up here.
      </Text>
    </Flex>
  )
}

function ThreadError({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="sm"
      className={styles.state}
      role="alert"
    >
      <Text weight="medium">Messages could not be loaded</Text>
      <Text color="subtle" align="center" size="sm">
        Check your connection and try again.
      </Text>
      <Button variant="secondary" size="small" onClick={onRetry}>
        Try Again
      </Button>
    </Flex>
  )
}
